import React, { useState, useEffect } from 'react';
import { IS_RECMAPPER, BRAND } from './product';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { hasSearch } from './utils/searchAccess';
import AppLink from './components/AppLink';

interface Folder {
  id: number;
  name: string;
  color: string | null;
  parent_folder_id: number | null;
  project_count: number;
}

// Helper to get all descendant folder IDs (prevent moving folder into itself)
const getDescendantIds = (folderId: number, folders: Folder[]): Set<number> => {
  const ids = new Set<number>();
  const queue = [folderId];
  while (queue.length > 0) {
    const current = queue.shift()!;
    folders.filter(f => f.parent_folder_id === current).forEach(f => {
      ids.add(f.id);
      queue.push(f.id);
    });
  }
  return ids;
};

export const ProjectDashboard: React.FC = () => {
  const { user, token, projects, createProject, deleteProject, loadProjects, isLoadingProjects, isLoadingUser, logout } = useAuth();
  const navigate = useNavigate();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showCreateFolderForm, setShowCreateFolderForm] = useState(false);
  const [newProject, setNewProject] = useState({ name: '', description: '' });
  const [newFolderName, setNewFolderName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [folders, setFolders] = useState<Folder[]>([]);
  const [expandedFolders, setExpandedFolders] = useState<Set<number>>(new Set());
  const [expandedProjects, setExpandedProjects] = useState<Set<number>>(new Set());
  const [moveProjectModal, setMoveProjectModal] = useState<{ show: boolean; projectId: number | null; projectName: string }>({
    show: false,
    projectId: null,
    projectName: ''
  });
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; projectId: number | null; projectName: string }>({
    show: false,
    projectId: null,
    projectName: ''
  });
  const [renameModal, setRenameModal] = useState<{ show: boolean; projectId: number | null; currentName: string; currentDescription: string }>({
    show: false,
    projectId: null,
    currentName: '',
    currentDescription: ''
  });
  const [renameName, setRenameName] = useState('');
  const [renameDescription, setRenameDescription] = useState('');
  const [renaming, setRenaming] = useState(false);

  // Fetch folders
  useEffect(() => {
    const fetchFolders = async () => {
      try {
        const response = await fetch('/api/folders', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setFolders(data);
          // Expand all folders by default
          setExpandedFolders(new Set(data.map((f: Folder) => f.id)));
        }
      } catch (err) {
        console.error('Error fetching folders:', err);
      }
    };
    if (token) fetchFolders();
  }, [token]);

  const toggleFolder = (folderId: number) => {
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) {
        newSet.delete(folderId);
      } else {
        newSet.add(folderId);
      }
      return newSet;
    });
  };

  const toggleProject = (projectId: number) => {
    setExpandedProjects(prev => {
      const newSet = new Set(prev);
      if (newSet.has(projectId)) {
        newSet.delete(projectId);
      } else {
        newSet.add(projectId);
      }
      return newSet;
    });
  };

  const handleCreateFolder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newFolderName.trim()) return;

    setLoading(true);
    try {
      const response = await fetch('/api/folders', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: newFolderName.trim() })
      });

      if (response.ok) {
        const folder = await response.json();
        setFolders([...folders, folder]);
        setExpandedFolders(prev => {
          const newSet = new Set(prev);
          newSet.add(folder.id);
          return newSet;
        });
        setNewFolderName('');
        setShowCreateFolderForm(false);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to create folder');
      }
    } catch (err) {
      setError('Failed to create folder');
    } finally {
      setLoading(false);
    }
  };

  const handleMoveProject = async (folderId: number | null, parentProjectId: number | null = null) => {
    if (!moveProjectModal.projectId) return;

    setLoading(true);
    try {
      const response = await fetch('/api/folders/move-project', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          projectId: moveProjectModal.projectId,
          folderId: folderId,
          parentProjectId: parentProjectId
        })
      });

      if (response.ok) {
        // Refresh projects to get updated folder assignments
        if (loadProjects) await loadProjects();
        // Refresh folders to update counts
        const foldersResponse = await fetch('/api/folders', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (foldersResponse.ok) {
          setFolders(await foldersResponse.json());
        }
        setMoveProjectModal({ show: false, projectId: null, projectName: '' });
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to move project');
      }
    } catch (err) {
      setError('Failed to move project');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFolder = async (folderId: number) => {
    if (!window.confirm('Delete this folder? Projects inside will be moved to root.')) return;

    try {
      const response = await fetch(`/api/folders/${folderId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setFolders(folders.filter(f => f.id !== folderId));
        if (loadProjects) await loadProjects();
      }
    } catch (err) {
      setError('Failed to delete folder');
    }
  };

  // My projects / All projects (team members). "Mine" = created by me,
  // starred by me, or I receive its monitoring / job-watch alerts (the API's
  // `owned` and `linked` flags). A parent project of one of mine stays
  // visible so the mine one isn't hidden under it. Remembered per user;
  // defaults to All so a new joiner sees everything, stars what they work
  // on, then switches.
  const onTeam = !!(user as any)?.teamId;
  const scopeKey = `mm.projects.scope.${user?.id || 'anon'}`;
  const [scope, setScopeState] = useState<'mine' | 'all'>(() => {
    try { return window.localStorage.getItem(scopeKey) === 'mine' ? 'mine' : 'all'; } catch { return 'all'; }
  });
  const setScope = (v: 'mine' | 'all') => {
    setScopeState(v);
    try { window.localStorage.setItem(scopeKey, v); } catch { /* per-browser preference only */ }
  };
  const isMine = (p: any) => p.owned === true || p.linked === true;
  const mineCount = projects.filter(isMine).length;
  const showMine = onTeam && scope === 'mine';
  const visibleIds = new Set<number>();
  if (showMine) {
    for (const p of projects as any[]) {
      if (!isMine(p)) continue;
      visibleIds.add(p.id);
      if (p.parent_project_id) visibleIds.add(p.parent_project_id);
    }
  }
  const scopedProjects = showMine ? projects.filter(p => visibleIds.has(p.id)) : projects;

  // Active in the last 30 / 90 days / All (Alex, 21 Oct 2026): by when the
  // project was last touched — people added, a move detected, opened,
  // renamed — not when it was created. Applies inside My / All projects and
  // inside folders; a parent stays visible when one of its sub-projects is
  // active. Remembered per user; defaults to 30 days.
  const windowKey = `mm.projects.window.${user?.id || 'anon'}`;
  const [activeWindow, setActiveWindowState] = useState<30 | 90 | 0>(() => {
    try {
      const v = parseInt(window.localStorage.getItem(windowKey) || '30', 10);
      return v === 90 || v === 0 ? v : 30;
    } catch { return 30; }
  });
  const setActiveWindow = (v: 30 | 90 | 0) => {
    setActiveWindowState(v);
    try { window.localStorage.setItem(windowKey, String(v)); } catch { /* per-browser preference only */ }
  };
  const activityOf = (p: any): number => {
    const t = new Date(p.last_activity_at || p.updated_at || p.created_at).getTime();
    return Number.isFinite(t) ? t : 0;
  };
  const activeSince = (days: number) => Date.now() - days * 86400000;
  const activeWithin = (days: number) => scopedProjects.filter(p => activityOf(p) >= activeSince(days)).length;
  const windowIds = new Set<number>();
  if (activeWindow) {
    const cutoff = activeSince(activeWindow);
    for (const p of scopedProjects as any[]) {
      if (activityOf(p) < cutoff) continue;
      windowIds.add(p.id);
      if (p.parent_project_id) windowIds.add(p.parent_project_id);
    }
  }
  const visibleProjects = activeWindow ? scopedProjects.filter(p => windowIds.has(p.id)) : scopedProjects;
  const hiddenByWindow = scopedProjects.length - visibleProjects.length;

  // Organize projects by folder, filtering out sub-projects (they render under their parent)
  // Starred projects float to the top, then the most recently active.
  const byStar = (a: any, b: any) =>
    ((b.starred === true ? 1 : 0) - (a.starred === true ? 1 : 0)) || (activityOf(b) - activityOf(a));
  const relativeActivity = (p: any): string => {
    const ms = Date.now() - activityOf(p);
    const d = Math.floor(ms / 86400000);
    if (ms < 3600000) return 'Just now';
    if (d < 1) return `${Math.max(1, Math.floor(ms / 3600000))}h ago`;
    if (d === 1) return 'Yesterday';
    if (d < 30) return `${d} days ago`;
    if (d < 365) return `${Math.floor(d / 30)} month${Math.floor(d / 30) === 1 ? '' : 's'} ago`;
    return `${Math.floor(d / 365)} year${Math.floor(d / 365) === 1 ? '' : 's'} ago`;
  };
  const rootProjects = visibleProjects.filter(p => !p.folder_id && !p.parent_project_id).slice().sort(byStar);
  const projectsByFolder = folders.reduce((acc, folder) => {
    acc[folder.id] = visibleProjects.filter(p => p.folder_id === folder.id && !p.parent_project_id).slice().sort(byStar);
    return acc;
  }, {} as Record<number, typeof projects>);
  // Sub-projects grouped by parent project ID
  const childProjects = (parentId: number) => visibleProjects.filter(p => p.parent_project_id === parentId);

  // Build folder tree (nested folders). In "My projects", folders with
  // nothing of mine anywhere inside them are hidden.
  const folderHasVisible = (folderId: number): boolean =>
    (projectsByFolder[folderId] || []).length > 0
    || folders.some(f => f.parent_folder_id === folderId && folderHasVisible(f.id));
  const rootFolders = folders.filter(f => !f.parent_folder_id && (!showMine || folderHasVisible(f.id)));
  const childFolders = (parentId: number) => folders.filter(f => f.parent_folder_id === parentId && (!showMine || folderHasVisible(f.id)));

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await createProject(newProject.name, newProject.description);
      setNewProject({ name: '', description: '' });
      setShowCreateForm(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create project');
    } finally {
      setLoading(false);
    }
  };

  // "Keep growing" (weekly refresh of the project's saved searches).
  const toggleAutoRefresh = async (projectId: number, enabled: boolean) => {
    try {
      const r = await fetch(`/api/projects/${projectId}/auto-refresh`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { if (!d.trialBlocked) window.alert(d.error || 'Could not change Keep growing'); return; } // trial: the upgrade prompt opens itself
      loadProjects();
    } catch {
      window.alert('Could not change Keep growing');
    }
  };

  const handleDeleteClick = (projectId: number, projectName: string) => {
    setDeleteConfirm({ show: true, projectId, projectName });
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirm.projectId) return;

    setLoading(true);
    setError('');

    try {
      await deleteProject(deleteConfirm.projectId);
      setDeleteConfirm({ show: false, projectId: null, projectName: '' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete project');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirm({ show: false, projectId: null, projectName: '' });
  };

  const [duplicating, setDuplicating] = useState<number | null>(null);

  // Duplicate project — admin (user 1) only. Reuses the existing
  // /api/admin/duplicate-project endpoint, which copies companies (with
  // Clay-enriched office addresses), candidates, hiring managers, all the
  // associations, pin groups, and custom columns.
  const handleDuplicateClick = async (projectId: number, projectName: string) => {
    if (!user || user.id !== 1) return;
    if (!window.confirm(`Duplicate "${projectName}"? This will copy all candidates, companies, hiring managers, and their office addresses.`)) {
      return;
    }
    setDuplicating(projectId);
    setError('');
    try {
      const response = await fetch('/api/admin/duplicate-project', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ projectId, fromUserId: user.id })
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to duplicate project');
      }
      if (loadProjects) await loadProjects();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to duplicate project');
    } finally {
      setDuplicating(null);
    }
  };

  const handleRenameClick = (projectId: number, currentName: string, currentDescription: string) => {
    setRenameModal({ show: true, projectId, currentName, currentDescription });
    setRenameName(currentName);
    setRenameDescription(currentDescription || '');
  };

  const handleRename = async () => {
    if (!renameModal.projectId || !renameName.trim()) return;
    setRenaming(true);
    try {
      const response = await fetch(`/api/projects/${renameModal.projectId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: renameName.trim(),
          description: renameDescription.trim()
        })
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update project');
      }
      if (loadProjects) await loadProjects();
      setRenameModal({ show: false, projectId: null, currentName: '', currentDescription: '' });
      setRenameName('');
      setRenameDescription('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update project');
    } finally {
      setRenaming(false);
    }
  };

  // Render a project row + its sub-projects with collapsible dropdown
  const renderProjectRow = (project: typeof projects[0], paddingLeft: number, isSubProject: boolean = false): React.ReactNode => {
    const subProjects = childProjects(project.id);
    const hasSubProjects = subProjects.length > 0;
    const isExpanded = expandedProjects.has(project.id);

    return (
      <React.Fragment key={project.id}>
        <tr className={`hover:bg-gray-50 transition-all duration-200 ${isSubProject ? 'bg-blue-50/30' : 'bg-gray-50'} max-md:flex max-md:flex-wrap max-md:items-center`}>
          <td className="px-6 py-4 max-md:w-full max-md:pt-3 max-md:pb-1 max-md:pr-3" style={{ paddingLeft }}>
            <div className="flex items-center gap-1">
              {isSubProject && <span className="text-gray-400 text-xs mr-1">&#8627;</span>}
              {!isSubProject && (
                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    await fetch(`/api/projects/${project.id}/star`, {
                      method: 'POST',
                      headers: { 'Authorization': `Bearer ${token}` },
                    }).catch(() => {});
                    loadProjects();
                  }}
                  className={`p-0.5 mr-1 text-base leading-none transition-colors ${(project as any).starred ? 'text-amber-400 hover:text-amber-500' : 'text-gray-300 hover:text-amber-400'}`}
                  title={(project as any).starred ? 'Unstar — remove from My projects, the top of this list and your dashboard focus' : 'Star — add to My projects, pin to the top of this list and focus your dashboard on it'}
                >
                  {(project as any).starred ? '★' : '☆'}
                </button>
              )}
              {hasSubProjects && (
                <button
                  onClick={() => toggleProject(project.id)}
                  className="text-gray-400 hover:text-gray-600 p-0.5 mr-1 transition-transform"
                  title={isExpanded ? 'Collapse sub-projects' : `Show ${subProjects.length} sub-project${subProjects.length !== 1 ? 's' : ''}`}
                >
                  <svg
                    className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}
              <AppLink
                to={`/project/${project.id}`}
                className={`text-sm font-medium cursor-pointer text-left ${(project as any).hidden ? 'text-gray-400 line-through hover:text-gray-600' : 'text-blue-600 hover:text-blue-800'}`}
                title={(project as any).hidden ? 'Hidden from your team — only you and the team’s admins can see it' : undefined}
              >
                {project.name}
              </AppLink>
              {(project as any).hidden && (
                <span className="ml-1 px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 text-[10px] font-medium whitespace-nowrap">Hidden from team</span>
              )}
              {/* Team admins: hide / unhide for the rest of the team (migration 229) */}
              {!isSubProject && user?.teamRole === 'admin' && (project as any).in_team && (
                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    const hidden = !!(project as any).hidden;
                    const r = await fetch(`/api/projects/${project.id}/${hidden ? 'unhide' : 'hide'}`, {
                      method: 'POST',
                      headers: { 'Authorization': `Bearer ${token}` },
                    }).catch(() => null);
                    if (r && !r.ok) {
                      const d = await r.json().catch(() => ({}));
                      alert(d.error || 'Could not change who can see this project');
                    }
                    loadProjects();
                  }}
                  className={`ml-1 p-0.5 transition-colors ${(project as any).hidden ? 'text-gray-500 hover:text-blue-600' : 'text-gray-300 hover:text-gray-600'}`}
                  title={(project as any).hidden
                    ? 'Hidden from your team. Click to show it to everyone on the team again.'
                    : 'Hide from your team: members and viewers stop seeing this project; you, the other admins and its owner still can.'}
                  aria-label={(project as any).hidden ? 'Unhide project' : 'Hide project from team'}
                >
                  {(project as any).hidden ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                  )}
                </button>
              )}
              {hasSubProjects && (
                <button
                  onClick={() => toggleProject(project.id)}
                  className="text-xs text-gray-400 ml-1 hover:text-gray-600 cursor-pointer"
                >
                  ({subProjects.length} sub-project{subProjects.length !== 1 ? 's' : ''})
                </button>
              )}
            </div>
            {/* Keep growing (weekly refresh) — under the name, so the action
                buttons stay short and the table fits the page. */}
            <div>
                {(project as any).has_search && hasSearch(user as any) && (() => {
                  const on = !!(project as any).auto_refresh;
                  const scope = (project as any).search_scope || {};
                  const last = (project as any).auto_refresh_last_result;
                  const tip = [
                    on ? 'Keep growing is on: every week this project re-runs its saved search and adds people who newly match (tagged New for 30 days).'
                      : 'Keep growing: re-run this project\u2019s saved search every week and add people who newly match.',
                    scope.fixed ? `Its search covers a fixed list of ${scope.companies} companies, so it finds new people at those companies only.` : '',
                    last && last.at ? `Last run ${new Date(last.at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}: ${last.added || 0} added${last.stopped ? ` — ${last.stopped}` : ''}.` : '',
                  ].filter(Boolean).join(' ');
                  return (
                    <button
                      onClick={() => toggleAutoRefresh(project.id, !on)}
                      className={`mt-1 inline-flex items-center gap-1.5 text-xs text-left transition-colors ${on ? 'text-green-700 hover:text-green-800' : 'text-gray-500 hover:text-gray-700'}`}
                      title={tip}
                    >
                      <span className={`relative inline-flex h-4 w-7 flex-shrink-0 items-center rounded-full transition-colors ${on ? 'bg-green-500' : 'bg-gray-300'}`}>
                        <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${on ? 'translate-x-[14px]' : 'translate-x-[2px]'}`} />
                      </span>
                      Keep growing weekly{scope.fixed ? ` · ${scope.companies} companies` : ''}
                    </button>
                  );
                })()}
            </div>
          </td>
          <td className="px-6 py-4 max-md:hidden">
            <div
              className="text-sm text-gray-600 max-w-md whitespace-pre-wrap line-clamp-2 cursor-pointer hover:text-gray-900"
              onClick={() => handleRenameClick(project.id, project.name, project.description || '')}
              title={project.description ? 'Click to view or edit' : 'Click to add a description'}
            >
              {project.description || '-'}
            </div>
          </td>
          <td className="px-6 py-4 whitespace-nowrap max-md:px-4 max-md:pt-0 max-md:pb-1 max-md:w-full">
            <div
              className="text-sm text-gray-500 max-md:text-xs"
              title={`Last active ${new Date(activityOf(project)).toLocaleString()} · created ${new Date(project.created_at).toLocaleDateString()}`}
            >
              {relativeActivity(project)}
            </div>
          </td>
          <td className="px-6 py-4 text-right text-sm font-medium w-64 max-md:w-full max-md:text-left max-md:px-4 max-md:pt-1 max-md:pb-3 max-md:leading-7">
            <AppLink to={`/project/${project.id}`} className="text-blue-600 hover:text-blue-700 mr-3 transition-colors">View</AppLink>
            {(project as any).has_search && hasSearch(user as any) && (
              <AppLink
                to={`/search?fromProject=${project.id}`}
                className="text-teal-600 hover:text-teal-700 mr-3 transition-colors"
                title="Open the search this project was built from — edit it, then create a new project or add to an existing one"
              >
                Search
              </AppLink>
            )}
            <button onClick={() => handleRenameClick(project.id, project.name, project.description || '')} className="text-gray-500 hover:text-gray-700 mr-3 transition-colors">Edit</button>
            <button onClick={() => setMoveProjectModal({ show: true, projectId: project.id, projectName: project.name })} className="text-gray-500 hover:text-gray-700 mr-3 transition-colors">Move</button>
            {user?.id === 1 && (
              <button
                onClick={() => handleDuplicateClick(project.id, project.name)}
                disabled={duplicating === project.id}
                className="text-gray-500 hover:text-gray-700 mr-3 transition-colors disabled:opacity-50"
                title="Duplicate project (admin)"
              >
                {duplicating === project.id ? 'Duplicating…' : 'Duplicate'}
              </button>
            )}
            <button onClick={() => handleDeleteClick(project.id, project.name)} className="text-red-500 hover:text-red-700 transition-colors">Delete</button>
          </td>
        </tr>
        {/* Render sub-projects only when expanded */}
        {hasSubProjects && isExpanded && subProjects.map(child => renderProjectRow(child, paddingLeft + 28, true))}
      </React.Fragment>
    );
  };

  // Recursive folder + project rendering
  const renderFolderTree = (folderList: Folder[], depth: number): React.ReactNode => {
    return folderList.map((folder) => {
      const subFolders = childFolders(folder.id);
      const folderProjects = projectsByFolder[folder.id] || [];
      const paddingLeft = 24 + depth * 24;

      return (
        <React.Fragment key={`folder-${folder.id}`}>
          {/* Folder Row */}
          <tr className="bg-yellow-50 hover:bg-yellow-100 transition-all duration-200 max-md:flex max-md:items-center max-md:justify-between">
            <td colSpan={3} className="px-6 py-3" style={{ paddingLeft }}>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => toggleFolder(folder.id)}
                  className="text-yellow-700 hover:text-yellow-900 p-1"
                >
                  {expandedFolders.has(folder.id) ? '📂' : '📁'}
                </button>
                <span className="font-medium text-yellow-800">{folder.name}</span>
                <span className="text-xs text-yellow-600">
                  ({folderProjects.length} project{folderProjects.length !== 1 ? 's' : ''})
                </span>
              </div>
            </td>
            <td className="px-6 py-3 text-right">
              <button
                onClick={() => handleDeleteFolder(folder.id)}
                className="text-red-500 hover:text-red-700 text-xs"
              >
                Delete Folder
              </button>
            </td>
          </tr>

          {/* Expanded content: subfolders + projects */}
          {expandedFolders.has(folder.id) && (
            <>
              {/* Sub-folders first */}
              {subFolders.length > 0 && renderFolderTree(subFolders, depth + 1)}

              {/* Projects in this folder (with sub-project nesting) */}
              {folderProjects.map((project) => renderProjectRow(project, paddingLeft + 28))}
            </>
          )}
        </React.Fragment>
      );
    });
  };

  // Recursive folder options for Move modal (includes projects within folders as parent options)
  const renderMoveOptions = (folderList: Folder[], depth: number): React.ReactNode => {
    return folderList.map(folder => {
      const subs = childFolders(folder.id);
      const folderProjects = (projectsByFolder[folder.id] || []).filter(p => p.id !== moveProjectModal.projectId);
      return (
        <React.Fragment key={folder.id}>
          <button
            onClick={() => handleMoveProject(folder.id, null)}
            className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:bg-yellow-50 transition-all duration-200 flex items-center gap-2"
            style={{ paddingLeft: 16 + depth * 20 }}
          >
            <span>📁</span> {folder.name}
          </button>
          {subs.length > 0 && renderMoveOptions(subs, depth + 1)}
          {/* Show projects within this folder as potential parents */}
          {folderProjects.map(p => (
            <button
              key={`proj-${p.id}`}
              onClick={() => handleMoveProject(folder.id, p.id)}
              className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:bg-blue-50 transition-all duration-200 flex items-center gap-2 text-sm"
              style={{ paddingLeft: 36 + depth * 20 }}
            >
              <span className="text-gray-400">&#8627;</span>
              <span className="text-blue-600">Under "{p.name}"</span>
            </button>
          ))}
        </React.Fragment>
      );
    });
  };

  // Show loading indicator while loading user or projects
  if (isLoadingUser || isLoadingProjects) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b max-md:hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              {user?.teamLogoUrl ? (
                <img
                  src={user.teamLogoUrl}
                  alt="Company Logo"
                  className="h-8 max-w-[180px] object-contain"
                />
              ) : (
                <h1 className="text-2xl font-bold tracking-tight text-gray-900">{BRAND.name}</h1>
              )}
              <span className="ml-4 text-gray-500">/ Projects</span>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">
                Welcome, {user?.firstName} {user?.lastName}
              </span>
              <button
                onClick={logout}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 max-md:px-0 max-md:py-0">
        {/* Project Selection */}
        <div className="card p-6 mb-8 max-md:p-3">
          <div className="flex justify-between items-center mb-4 max-md:flex-col max-md:items-stretch max-md:gap-3">
            <div className="flex items-center gap-4 flex-wrap">
              <h2 className="text-xl font-semibold tracking-tight">{showMine ? 'My Projects' : 'Your Projects'}</h2>
              {onTeam && (
                <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1" role="group" aria-label="Which projects to show">
                  {([['mine', `My projects (${mineCount})`], ['all', `All projects (${projects.length})`]] as const).map(([v, label]) => (
                    <button key={v} onClick={() => setScope(v)}
                      className={`px-3 py-1 rounded-md text-sm font-medium transition-colors max-md:px-2 max-md:text-xs max-md:whitespace-nowrap ${scope === v ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}
                      title={v === 'mine'
                        ? 'Projects you created, starred, or get monitoring / job-watch alerts for'
                        : "Every project in your team — star the ones you work on to add them to My projects"}>
                      {label}
                    </button>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1" role="group" aria-label="Active in the last" data-tour="projects-active-window">
                {([[30, `Active 30 days (${activeWithin(30)})`], [90, `90 days (${activeWithin(90)})`], [0, `All (${scopedProjects.length})`]] as const).map(([v, label]) => (
                  <button key={v} onClick={() => setActiveWindow(v)}
                    className={`px-3 py-1 rounded-md text-sm font-medium transition-colors max-md:px-2 max-md:text-xs max-md:whitespace-nowrap ${activeWindow === v ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}
                    title={v === 0
                      ? 'Every project, however old'
                      : `Projects touched in the last ${v} days — people added, a move detected, opened or renamed`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowCreateFolderForm(!showCreateFolderForm)}
                className="btn-secondary flex items-center gap-2"
              >
                <span>📁</span> New Folder
              </button>
              {!IS_RECMAPPER && <button
                onClick={() => setShowCreateForm(!showCreateForm)}
                className="btn-primary"
              >
                New Project
              </button>}
            </div>
          </div>

          {/* Create Folder Form */}
          {showCreateFolderForm && (
            <div className="mb-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <form onSubmit={handleCreateFolder} className="flex gap-3 items-end">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Folder Name
                  </label>
                  <input
                    type="text"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    placeholder="e.g., Tech Roles, Q1 Projects..."
                    required
                    className="input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-yellow-500 rounded-lg shadow-sm transition-all duration-200 hover:bg-yellow-600 hover:shadow-md hover:-translate-y-px active:translate-y-0 active:shadow-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-sm"
                >
                  {loading ? 'Creating...' : 'Create Folder'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateFolderForm(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </form>
            </div>
          )}

          {/* Create Project Form */}
          {showCreateForm && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <form onSubmit={handleCreateProject} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Project Name
                  </label>
                  <input
                    type="text"
                    value={newProject.name}
                    onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                    required
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={newProject.description}
                    onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                    rows={3}
                    className="input"
                  />
                </div>
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-red-800 text-sm">{error}</p>
                  </div>
                )}
                <div className="flex space-x-3">
                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary"
                  >
                    {loading ? 'Creating...' : 'Create Project'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCreateForm(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Project List with Folders */}
          <div>
            <table className="w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 max-md:hidden">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Project Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32" title="When the project was last touched: people added, a move detected, opened or renamed">
                    Last active
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-64">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {/* Render folders recursively */}
                {renderFolderTree(rootFolders, 0)}

                {/* Root projects (not in any folder, with sub-project nesting) */}
                {rootProjects.map((project) => renderProjectRow(project, 24))}
              </tbody>
            </table>
          </div>

          {activeWindow !== 0 && visibleProjects.length === 0 && scopedProjects.length > 0 && (
            <div className="text-center py-8">
              <p className="text-gray-700 font-medium">Nothing has been touched in the last {activeWindow} days.</p>
              <p className="text-gray-500 text-sm mt-1">
                {hiddenByWindow} older project{hiddenByWindow === 1 ? '' : 's'} {hiddenByWindow === 1 ? 'is' : 'are'} under{' '}
                <button className="text-blue-600 hover:underline" onClick={() => setActiveWindow(0)}>All</button>.
              </p>
            </div>
          )}

          {showMine && mineCount === 0 && projects.length > 0 && (
            <div className="text-center py-8">
              <p className="text-gray-700 font-medium">No projects of yours yet.</p>
              <p className="text-gray-500 text-sm mt-1">
                Switch to <button className="text-blue-600 hover:underline" onClick={() => setScope('all')}>All projects</button> and
                star ☆ the ones you work on — they'll appear here. Projects you create, or get monitoring and job alerts for, appear here too.
              </p>
            </div>
          )}

          {projects.length === 0 && folders.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-600">No projects yet. Create your first project to get started.</p>
            </div>
          )}
        </div>

        {/* Move Project Modal */}
        {moveProjectModal.show && (
          <div className="modal-backdrop flex items-center justify-center">
            <div className="modal-dialog p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold tracking-tight text-gray-900 mb-2">Move Project</h3>
              <p className="text-sm text-gray-600 mb-4">
                Move <strong>"{moveProjectModal.projectName}"</strong> to:
              </p>
              <div className="space-y-2 mb-4 max-h-[50vh] overflow-y-auto">
                <button
                  onClick={() => handleMoveProject(null, null)}
                  className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-all duration-200 flex items-center gap-2"
                >
                  <span>📋</span> Root (No folder, standalone)
                </button>
                {renderMoveOptions(rootFolders, 0)}
              </div>
              <button
                onClick={() => setMoveProjectModal({ show: false, projectId: null, projectName: '' })}
                className="btn-secondary w-full"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Edit Project Modal */}
        {renameModal.show && (
          <div className="modal-backdrop flex items-center justify-center">
            <div className="modal-dialog p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold tracking-tight text-gray-900 mb-4">Edit Project</h3>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                value={renameName}
                onChange={(e) => setRenameName(e.target.value)}
                placeholder="Project name"
                className="input mb-4"
                autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) handleRename(); }}
              />
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={renameDescription}
                onChange={(e) => setRenameDescription(e.target.value)}
                placeholder="Add a description (e.g. the boolean used to source this list)"
                rows={5}
                className="input mb-4"
              />
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setRenameModal({ show: false, projectId: null, currentName: '', currentDescription: '' });
                    setRenameName('');
                    setRenameDescription('');
                    setError('');
                  }}
                  disabled={renaming}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRename}
                  disabled={
                    renaming ||
                    !renameName.trim() ||
                    (renameName.trim() === renameModal.currentName &&
                      renameDescription.trim() === (renameModal.currentDescription || '').trim())
                  }
                  className="btn-primary"
                >
                  {renaming ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirm.show && (
          <div className="modal-backdrop flex items-center justify-center">
            <div className="modal-dialog p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold tracking-tight text-gray-900 mb-2">Delete Project</h3>
              <p className="text-sm text-gray-600 mb-4">
                Are you sure you want to delete <strong>"{deleteConfirm.projectName}"</strong>?
                This action cannot be undone and will permanently delete all associated data including
                candidates, companies, and hiring managers.
              </p>
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}
              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleCancelDelete}
                  disabled={loading}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  disabled={loading}
                  className="btn-danger"
                >
                  {loading ? 'Deleting...' : 'Delete Project'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
